import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from 'react'
import { Glyphs } from '../../glyphs'

export type WidgetSelectDropdownOption<T extends string> = {
	id: T
	label: ReactNode
}

export type WidgetSelectDropdownProps<T extends string> =
	| {
		options: ReadonlyArray<WidgetSelectDropdownOption<T>>
		value: T
		onChange: (value: T) => void
		label: ReactNode
		allowDefault?: false | undefined
	}
	| {
		options: ReadonlyArray<WidgetSelectDropdownOption<T>>
		value: T | ''
		onChange: (value: T | '') => void
		label: ReactNode
		allowDefault: true
	}

export function WidgetSelectDropdown<T extends string>({
	options,
	value,
	onChange,
	label,
	allowDefault,
}: WidgetSelectDropdownProps<T>) {
	return (
		<div className="text-sm text-grey-600 text-left bg-white ring ring-grey-200 rounded-sm shadow-sm">
			<Listbox value={value} onChange={onChange}>
				<ListboxButton className="text-left py-1 px-2 grid grid-cols-[1fr_auto] gap-1 w-full">
					{value === '' && allowDefault
						? 'Default'
						: options.find((option) => option.id === value)?.label}
					<div className="flex items-center justify-center relative">
						<Glyphs.micro.DownChevron className="scale-75 group-data-[open]:rotate-180 transition-transform duration-300" />
					</div>
				</ListboxButton>

				<ListboxOptions
					anchor="bottom"
					className="z-50 bg-white min-w-[calc(var(--button-width)+8px)] [--anchor-gap:8px] ring-1 py-1 ring-grey-100 shadow-md rounded text-sm text-grey-600"
				>
					{allowDefault && (
						<>
							<ListboxOption
								key="__default__"
								value=""
								data-prevent-focus-loss
								className={ctl(`
									relative
									group
									data-[selected]:bg-blue-50
									data-[selected]:text-blue-600
									cursor-pointer data-focus:bg-blue-100 mx-1 px-2 py-1 rounded pr-7
								`)}
							>
								Default
								<div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 opacity-0 group-data-[selected]:opacity-100">
									<Glyphs.heroiconsMicro.CheckIcon />
								</div>
							</ListboxOption>
							<div role="none" className="my-1 mx-1 border-t border-grey-100" />
						</>
					)}
					{options.map((option) => (
						<ListboxOption
							key={option.id}
							value={option.id}
							data-prevent-focus-loss
							className={ctl(`
								relative
								group
								data-[selected]:bg-blue-50
								data-[selected]:text-blue-600
								cursor-pointer data-focus:bg-blue-100 mx-1 px-2 py-1 rounded pr-7
							`)}
						>
							{option.label}
							<div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 opacity-0 group-data-[selected]:opacity-100">
								<Glyphs.heroiconsMicro.CheckIcon />
							</div>
						</ListboxOption>
					))}
				</ListboxOptions>
			</Listbox>
		</div>
	)
}
